import {
  formatCurrency,
  transformDateToLocalDateString,
} from "helpers";
import { FleetVehicleOfferRentRow } from "services/api/fleet/vehicles/offers/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { PictureIcon } from "ui/components/Pictures";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Badge from "ui/components/Badge/Badge";
import { getStatusBadgeVariant } from "services/api/offers/consts";
import Tooltip from "ui/components/Tooltip/Tooltip";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import ContactCell from "ui/components/Table/TableSearch/components/Cells/ContactCell";


export const OFFER_RENT_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleVehicleClick: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void,
  handleContactClick: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string
  ) => void,
  handleDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void
): Column[] => [
    {
      title: "",
      key: "offer_pdf",
      field: "",
      alwaysVisible: true,
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        if (!row.pdfUpload) return null;
        // Klik otvara generiranu PDF ponudu u novom tabu.
        return (
          <PictureIcon
            url={row.pdfUpload.url}
            fileName={row.pdfUpload.originalFileName}
            tooltip={row.pdfUpload.originalFileName}
          />
        );
      },
    },
    {
      title: "admin.finance.rent.search.offernumber",
      field: "offerNumber",
      type: ColumnTypes.string,
    },
    {
      title: "admin.finance.offer.rent.search.client.name",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        return (
          <ContactCell
            contact={row.contactClient}
            onClick={(e) => row.contactClient?.id && handleContactClick(e, row.contactClient.id)}
          />
        );
      },
    },
    {
      title: "admin.fleet.vehicle.offer.rent.search.status",
      key: "offer_status",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        if (!row.status) return null;
        return (
          <Badge
            text={row.status.name}
            variant={getStatusBadgeVariant(row.status.color)}
            height="small"
            showDot={true}
          />
        );
      },
    },
    {
      title: "admin.finance.offer.rent.search.vehicle",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        return (
          <VehicleCell
            vehicle={row.vehicle}
            onClick={(e) => row.vehicle?.id && handleVehicleClick(e, row.vehicle.id)}
          />
        );
      },
    },
    {
      title: "admin.finance.offer.rent.search.offerdate",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        return (
          <div>{transformDateToLocalDateString(row.offerDate)}</div>
        );
      },
    },
    {
      title: "admin.finance.offer.rent.search.duedate",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        const dateStr = transformDateToLocalDateString(row.dueDate);
        if (!row.durationDays) return <>{dateStr}</>;
        return (
          <Tooltip decorate={<span>{dateStr}</span>} position="bottom-left" size="medium">
            <div className="min-w-[200px]">
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji dospijeća</div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Datum dospijeća</span>
                <span className="font-medium">{dateStr}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Broj dana</span>
                <span className="font-bold text-sky-400">{row.durationDays}</span>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "admin.finance.offer.rent.search.total",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => (
        <Tooltip
          decorate={<span className="text-md font-medium">{formatCurrency.format(row.total ?? 0)}</span>}
          position="bottom-right"
          size="medium"
        >
          <div className="min-w-[240px]">
            <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled iznosa</div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">Cijena bez PDV-a</span>
              <span className="font-medium">{formatCurrency.format(row.totalWithoutTax ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300">PDV</span>
              <span className="font-medium">{formatCurrency.format(row.totalTax ?? 0)}</span>
            </div>
            {(row.totalDiscount ?? 0) > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Popust</span>
                <span className="font-medium text-amber-400">-{formatCurrency.format(row.totalDiscount ?? 0)}</span>
              </div>
            )}
            <div className="border-t border-slate-600 my-2"></div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">Ukupno s PDV-om</span>
              <span className="font-bold text-green-400">{formatCurrency.format(row.total ?? 0)}</span>
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Kreirao",
      key: "offer_created_by",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        return (
          <div className="text-sm text-slate-500">{row.userCreatedName ?? "-"}</div>
        );
      },
    },
    {
      title: "",
      field: "",
      type: ColumnTypes.custom,
      render: (row: FleetVehicleOfferRentRow) => {
        return (
          <ButtonMenu
            endIcon="/media/icons/more-dots.svg"
            cssClassName="h-full"
            variant="text"
          >
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ];
